import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import ManualHero from "../components/site/ManualHero";
import { api, API, formatApiError } from "../lib/api";

export default function Manual() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeUnit, setActiveUnit] = useState(0);
  const [tocOpen, setTocOpen] = useState(false);
  const [completed, setCompleted] = useState(() => new Set());

  const storageKey = `abril.manual.${token}.completed`;

  // Load completed set from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) setCompleted(new Set(JSON.parse(raw)));
    } catch (_) { /* ignore */ }
  }, [storageKey]);

  const persist = useCallback((next) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(Array.from(next)));
    } catch (_) { /* ignore */ }
  }, [storageKey]);

  const toggleComplete = useCallback((unitNumber) => {
    setCompleted((prev) => {
      const next = new Set(prev);
      const wasComplete = next.size;
      if (next.has(unitNumber)) {
        next.delete(unitNumber);
        toast("Unidad marcada como pendiente", { duration: 1600 });
      } else {
        next.add(unitNumber);
        // Special toast if this completes the whole manual
        if (next.size === 10 && wasComplete < 10) {
          toast.success("¡Terminaste el método! Tu diploma está listo abajo.", { duration: 3200 });
        } else {
          toast.success("Unidad terminada · Bien hecho", { duration: 1800 });
        }
      }
      persist(next);
      return next;
    });
  }, [persist]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await api.get(`/manual/${token}`);
        if (!cancel) setData(res.data);
      } catch (err) {
        if (!cancel) setError(formatApiError(err));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [token]);

  // Reset scroll on unit change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTocOpen(false);
  }, [activeUnit]);

  const pdfUrl = useMemo(() => `${API}/manual/${token}/pdf`, [token]);
  const diplomaUrl = useMemo(() => `${API}/manual/${token}/diploma`, [token]);

  const handleUnlock = useCallback(
    async (unitNumber, password) => {
      const clean = (password || "").trim().toUpperCase();
      if (!clean) return { ok: false, error: "Escribe la contraseña" };
      try {
        const res = await api.post(`/manual/${token}/unlock`, {
          unit_number: unitNumber,
          password: clean,
        });
        const nextList = res.data.unlocked_units || [];
        setData((prev) =>
          prev
            ? {
                ...prev,
                student: { ...prev.student, unlocked_units: nextList },
              }
            : prev
        );
        toast.success("Unidad desbloqueada", { duration: 1800 });
        return { ok: true };
      } catch (err) {
        const msg = formatApiError(err) || "Contraseña incorrecta";
        toast.error(msg, { duration: 1800 });
        return { ok: false, error: msg };
      }
    },
    [token]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center" data-testid="manual-loading">
        <p className="overline text-[#8B6D5E] animate-pulse">— Abriendo tu manual…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]" data-testid="manual-error">
        <Header />
        <section className="px-6 sm:px-10 pt-32 pb-24 max-w-xl mx-auto text-center">
          <p className="overline text-[#8B6D5E] mb-4">— Acceso</p>
          <h1 className="font-serif text-4xl sm:text-5xl leading-[1.02] mb-4">
            El link no funciona.
          </h1>
          <p className="text-[#3D3D3D] mb-8 leading-relaxed">
            {error || "No pudimos abrir tu manual. Es posible que el acceso esté pausado o que el link sea incorrecto. Escríbele directamente a Abril y ella lo resuelve en minutos."}
          </p>
          <Link
            to="/"
            className="inline-block bg-[#1A1A1A] text-[#FAFAFA] px-8 py-4 overline hover:bg-[#8B6D5E] transition-colors"
            data-testid="manual-back-home"
          >
            Volver al inicio
          </Link>
        </section>
        <Footer />
      </div>
    );
  }

  const { student, manual } = data;
  const units = manual.units;
  const unit = units[activeUnit];
  const completedCount = completed.size;
  const totalCount = units.length;
  const isUnitDone = completed.has(unit.number);
  const unlockedSet = new Set(student.unlocked_units || []);
  const isUnitLocked = !unlockedSet.has(unit.number);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="manual-page">
      <Helmet>
        <title>{`Manual — ${student.name} · Academia Abril Suárez`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      {/* ── Hero editorial ── */}
      <ManualHero
        student={student}
        subtitle={manual.subtitle}
        pdfUrl={pdfUrl}
        completedCount={completedCount}
        totalCount={totalCount}
        onOpenToc={() => setTocOpen(true)}
        onExplore={() => {
          const el = document.getElementById("manual-content");
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }}
      />

      {/* ── Body: TOC + Content ── */}
      <section id="manual-content" className="px-4 sm:px-10 py-16 border-t border-[#E8E1DA] bg-[#FAFAFA]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-[280px_1fr] gap-10">
          {/* Desktop TOC */}
          <aside className="hidden md:block" data-testid="manual-toc-desktop">
            <TocList
              units={units}
              activeUnit={activeUnit}
              onSelect={setActiveUnit}
              completed={completed}
              unlocked={unlockedSet}
            />
          </aside>

          {/* Content */}
          <article
            key={unit.number}
            className="min-w-0 animate-[fadein_.6s_ease]"
            data-testid={`manual-unit-${unit.number}`}
          >
            {isUnitLocked ? (
              <LockedUnitView
                unit={unit}
                totalUnits={units.length}
                onUnlock={(pw) => handleUnlock(unit.number, pw)}
                onNav={setActiveUnit}
                activeIndex={activeUnit}
              />
            ) : (
              <UnitView
                unit={unit}
                totalUnits={units.length}
                onNav={setActiveUnit}
                activeIndex={activeUnit}
                isDone={isUnitDone}
                onToggleComplete={toggleComplete}
              />
            )}

            {/* Closing after last unit */}
            {activeUnit === units.length - 1 && (
              <div className="mt-16 pt-14 border-t border-[#E8E1DA]">
                <p className="overline text-[#8B6D5E] mb-3">— Cierre</p>
                <h2 className="font-serif text-4xl leading-[1.05] mb-6">
                  Bienvenida, {student.first_name}.{" "}
                  <em className="text-[#8B6D5E]">Al oficio.</em>
                </h2>
                {manual.closing.split("\n\n").map((p, i) => (
                  <p key={i} className="text-[#3D3D3D] leading-relaxed text-lg mb-4">
                    {p}
                  </p>
                ))}
                <p className="mt-8 font-serif italic text-2xl text-[#1A1A1A]">
                  {manual.signature}
                </p>

                {/* Diploma CTA — visible cuando la alumna marcó todas las unidades */}
                {completedCount >= totalCount && (
                  <div
                    className="mt-14 border border-[#3C6348] bg-[#F0F4F1] p-8 sm:p-10 relative overflow-hidden"
                    data-testid="manual-diploma-block"
                  >
                    <p className="overline text-[#3C6348] mb-3">— Diploma disponible</p>
                    <h3 className="font-serif text-3xl sm:text-4xl leading-tight mb-4">
                      Lo lograste, {student.first_name}.
                      <br />
                      <em className="text-[#3C6348]">Aquí está tu diploma.</em>
                    </h3>
                    <p className="text-[#3D3D3D] leading-relaxed max-w-xl mb-6">
                      Terminaste las diez unidades del método. Este diploma editorial lleva tu
                      nombre y la fecha en la que cerraste el programa. Guárdalo, imprímelo o
                      compártelo — lo ganaste.
                    </p>
                    <a
                      href={diplomaUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-3 bg-[#3C6348] text-white overline px-6 py-4 hover:bg-[#1A1A1A] transition-colors"
                      data-testid="manual-diploma-download"
                    >
                      <span className="inline-flex items-center justify-center w-4 h-4 border border-white/60 text-[10px] leading-none">
                        ★
                      </span>
                      Descargar diploma
                    </a>
                  </div>
                )}
              </div>
            )}
          </article>
        </div>
      </section>

      {/* Mobile TOC modal */}
      {tocOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 bg-[#1A1A1A]/70 flex items-end"
          onClick={() => setTocOpen(false)}
          data-testid="manual-toc-mobile"
        >
          <div
            className="w-full bg-[#FAFAFA] max-h-[85vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <p className="overline text-[#8B6D5E]">— Índice</p>
              <button
                onClick={() => setTocOpen(false)}
                className="text-2xl leading-none text-[#1A1A1A]"
                data-testid="manual-close-toc-mobile"
                aria-label="Cerrar índice"
              >
                ×
              </button>
            </div>
            <TocList
              units={units}
              activeUnit={activeUnit}
              onSelect={setActiveUnit}
              completed={completed}
              unlocked={unlockedSet}
            />
          </div>
        </div>
      )}

      <Footer />

      <style>{`
        @keyframes fadein { from { opacity:0; transform: translateY(6px);} to { opacity:1; transform: none;} }
        .overline { font-family: Outfit, sans-serif; text-transform: uppercase; letter-spacing:.24em; font-size:11px; font-weight:600; }
        .font-serif { font-family: 'Cormorant Garamond', 'Times New Roman', serif; }
      `}</style>
    </div>
  );
}

/* ─────────────────────────── */
function TocList({ units, activeUnit, onSelect, completed, unlocked }) {
  return (
    <div className="md:sticky md:top-28 space-y-1" data-testid="manual-toc-list">
      <p className="overline text-[#8B6D5E] mb-6">— Contenido</p>
      {units.map((u, i) => {
        const active = i === activeUnit;
        const done = completed?.has(u.number);
        const locked = unlocked && !unlocked.has(u.number);
        return (
          <button
            key={u.number}
            onClick={() => onSelect(i)}
            className={`w-full text-left group transition-all border-l-2 pl-4 py-3 ${
              active
                ? "border-[#8B6D5E] bg-[#F7F0E8]"
                : done
                ? "border-[#3C6348]/50 hover:bg-[#F0F4F1]"
                : locked
                ? "border-transparent hover:border-[#8B6D5E]/40 hover:bg-[#F7F0E8]/40 opacity-70"
                : "border-transparent hover:border-[#8B6D5E]/40 hover:bg-[#F7F0E8]/40"
            }`}
            data-testid={`manual-toc-item-${u.number}`}
          >
            <div className="flex items-baseline gap-3">
              {done ? (
                <span
                  className="w-8 flex-none flex items-center justify-start"
                  data-testid={`manual-toc-check-${u.number}`}
                >
                  <span
                    className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#3C6348] text-white text-[9px] font-bold leading-none"
                    aria-label="Unidad terminada"
                  >
                    ✓
                  </span>
                </span>
              ) : locked ? (
                <span
                  className="w-8 flex-none flex items-center justify-start"
                  data-testid={`manual-toc-lock-${u.number}`}
                >
                  <Lock size={12} className="text-[#8B6D5E]" aria-label="Unidad bloqueada" />
                </span>
              ) : (
                <span className="overline text-[#8B6D5E] w-8">{String(u.number).padStart(2, "0")}</span>
              )}
              <span
                className={`font-serif text-lg leading-tight ${
                  active
                    ? "text-[#1A1A1A]"
                    : done
                    ? "text-[#3C6348]"
                    : locked
                    ? "text-[#1A1A1A]/60"
                    : "text-[#1A1A1A]/85"
                }`}
              >
                {u.title}
              </span>
            </div>
            <p
              className={`text-xs mt-1 ml-11 ${
                done ? "text-[#3C6348]/70" : locked ? "text-[#8B6D5E]/70" : "text-[#5C5C5C]"
              }`}
            >
              {done ? "Terminada" : locked ? "Bloqueada · pide contraseña a Abril" : u.eyebrow}
            </p>
          </button>
        );
      })}
    </div>
  );
}

/* ─────────────────────────── */
function LockedUnitView({ unit, totalUnits, onUnlock, onNav, activeIndex }) {
  const [pw, setPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // reset when switching to another locked unit
    setPw("");
    setError(null);
  }, [unit.number]);

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!pw.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    const res = await onUnlock(pw);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      setPw("");
      inputRef.current?.focus?.();
    }
  }, [pw, onUnlock, submitting]);

  return (
    <div className="max-w-3xl" data-testid={`manual-locked-${unit.number}`}>
      <p className="overline text-[#8B6D5E] mb-4">
        — Unidad {String(unit.number).padStart(2, "0")} · {unit.eyebrow}
      </p>
      <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight mb-6 text-[#1A1A1A]/60">
        {unit.title}
      </h2>

      <div className="border border-[#E8E1DA] bg-[#F7F0E8]/50 p-8 sm:p-12 my-8" data-testid="manual-lock-card">
        <div className="flex items-center gap-4 mb-6">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#1A1A1A] text-white">
            <Lock size={20} />
          </span>
          <div>
            <p className="overline text-[#8B6D5E]">— Unidad bloqueada</p>
            <p className="font-serif text-2xl mt-1">Escribe la contraseña que Abril te envió.</p>
          </div>
        </div>
        <p className="text-[#3D3D3D] leading-relaxed max-w-lg mb-8">
          Cada unidad se abre con su propia contraseña de 6 letras. Recibirás cada
          código por correo cuando Abril libere la siguiente unidad. Una vez abierta,
          la unidad queda disponible para siempre en tu portal.
        </p>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-md">
          <input
            ref={inputRef}
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            value={pw}
            onChange={(e) => setPw(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12))}
            placeholder="ABCDEF"
            aria-label="Contraseña de la unidad"
            className="flex-1 bg-white border border-[#E8E1DA] focus:border-[#1A1A1A] outline-none px-4 py-3 font-mono tracking-[0.25em] text-lg text-[#1A1A1A] placeholder:text-[#1A1A1A]/25"
            data-testid="manual-lock-input"
          />
          <button
            type="submit"
            disabled={submitting || !pw.trim()}
            className="bg-[#1A1A1A] text-white overline px-6 py-3 hover:bg-[#8B6D5E] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="manual-lock-submit"
          >
            {submitting ? "Abriendo…" : "Desbloquear"}
          </button>
        </form>
        {error && (
          <p className="mt-3 text-sm text-[#9E3C3C]" data-testid="manual-lock-error">
            {error}
          </p>
        )}
      </div>

      {/* Navigation */}
      <div className="mt-12 pt-10 border-t border-[#E8E1DA] flex items-center justify-between gap-4">
        <button
          disabled={activeIndex === 0}
          onClick={() => onNav(activeIndex - 1)}
          className="overline text-[#8B6D5E] hover:text-[#1A1A1A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          data-testid="manual-nav-prev"
        >
          ← Unidad anterior
        </button>
        <span className="overline text-[#5C5C5C]" data-testid="manual-nav-position">
          {String(activeIndex + 1).padStart(2, "0")} / {String(totalUnits).padStart(2, "0")}
        </span>
        <button
          disabled={activeIndex === totalUnits - 1}
          onClick={() => onNav(activeIndex + 1)}
          className="overline text-[#8B6D5E] hover:text-[#1A1A1A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          data-testid="manual-nav-next"
        >
          Siguiente unidad →
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────── */
function UnitView({ unit, totalUnits, onNav, activeIndex, isDone, onToggleComplete }) {
  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <p className="overline text-[#8B6D5E]" data-testid="unit-number">
          — Unidad {String(unit.number).padStart(2, "0")} · {unit.eyebrow}
        </p>
        {isDone && (
          <span
            className="inline-flex items-center gap-2 bg-[#3C6348] text-white px-3 py-1 overline text-[10px]"
            data-testid="unit-done-badge"
          >
            <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-white text-[#3C6348] text-[8px] font-bold leading-none">
              ✓
            </span>
            Terminada
          </span>
        )}
      </div>
      <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight mb-8" data-testid="unit-title">
        {unit.title}
      </h2>
      <div className="w-20 h-[2px] bg-[#8B6D5E] mb-8"></div>

      <p className="italic text-xl text-[#1A1A1A] leading-relaxed mb-10">{unit.intro}</p>

      {/* Objectives */}
      <div className="bg-[#F7F0E8] p-8 mb-12" data-testid="unit-objectives">
        <p className="overline text-[#8B6D5E] mb-5">— Objetivos</p>
        <ol className="space-y-3">
          {unit.objectives.map((o, i) => (
            <li key={i} className="flex gap-4 items-start text-[#1A1A1A] leading-relaxed">
              <span className="overline text-[#8B6D5E] flex-none pt-1">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span>{o}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Sections */}
      <div className="space-y-14" data-testid="unit-sections">
        {unit.sections.map((s, i) => (
          <section key={i} className="border-t border-[#E8E1DA] pt-10 first:border-0 first:pt-0">
            <h3 className="font-serif text-2xl sm:text-3xl leading-tight mb-6 text-[#1A1A1A]">
              {s.heading}
            </h3>
            <div className="prose-content space-y-5 text-[#3D3D3D] text-lg leading-[1.75]">
              {s.body.split("\n\n").map((p, k) => (
                <p key={k} dangerouslySetInnerHTML={{ __html: p.replace(/\n/g, "<br/>") }} />
              ))}
            </div>

            {s.tips && s.tips.length > 0 && (
              <div className="mt-8 border-l-2 border-[#8B6D5E] pl-6 space-y-3" data-testid={`section-tips-${i}`}>
                <p className="overline text-[#8B6D5E]">Tips de Abril</p>
                {s.tips.map((t, j) => (
                  <p key={j} className="text-[#1A1A1A] italic leading-relaxed">
                    <span className="text-[#8B6D5E] font-bold not-italic">·&nbsp;</span>
                    {t}
                  </p>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>

      {/* Key insight */}
      {unit.key_insight && (
        <blockquote className="mt-14 bg-[#1A1A1A] text-[#FAFAFA] p-10 sm:p-14" data-testid="unit-insight">
          <p className="overline text-[#B08D5D] mb-4">— Insight</p>
          <p className="font-serif italic text-2xl sm:text-3xl leading-[1.3]">
            &ldquo;{unit.key_insight}&rdquo;
          </p>
        </blockquote>
      )}

      {/* Exercises */}
      {unit.exercises && unit.exercises.length > 0 && (
        <div className="mt-14" data-testid="unit-exercises">
          <p className="overline text-[#8B6D5E] mb-5">— Ejercicios</p>
          <ol className="space-y-4">
            {unit.exercises.map((e, i) => (
              <li key={i} className="flex gap-5 items-start p-5 border border-[#E8E1DA] hover:border-[#8B6D5E] transition-colors">
                <span className="overline text-[#8B6D5E] flex-none pt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-[#1A1A1A] leading-relaxed">{e}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Checklist */}
      {unit.checklist && unit.checklist.length > 0 && (
        <div className="mt-12" data-testid="unit-checklist">
          <p className="overline text-[#8B6D5E] mb-5">— Checklist de la unidad</p>
          <ul className="space-y-3">
            {unit.checklist.map((c, i) => (
              <li key={i} className="flex gap-4 items-start text-[#1A1A1A] leading-relaxed">
                <span className="text-[#8B6D5E] flex-none pt-1">◻</span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Reflection */}
      {unit.reflection && (
        <div className="mt-14 p-8 bg-[#F7F0E8]" data-testid="unit-reflection">
          <p className="overline text-[#8B6D5E] mb-4">— Reflexión</p>
          <p className="font-serif italic text-xl text-[#1A1A1A] leading-relaxed">
            {unit.reflection}
          </p>
        </div>
      )}

      {/* Mark-as-done CTA */}
      <div className="mt-14" data-testid="unit-complete-cta">
        {isDone ? (
          <div className="border border-[#3C6348] bg-[#F0F4F1] p-6 sm:p-7 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#3C6348] text-white text-sm font-bold leading-none">
                ✓
              </span>
              <div>
                <p className="overline text-[#3C6348]">— Unidad terminada</p>
                <p className="font-serif text-xl text-[#1A1A1A] mt-1">Bien hecho. Sigue con la siguiente.</p>
              </div>
            </div>
            <button
              onClick={() => onToggleComplete(unit.number)}
              className="overline text-[#3C6348] hover:text-[#1A1A1A] text-[10px] underline underline-offset-4 decoration-[#3C6348]/40 hover:decoration-[#1A1A1A]"
              data-testid="unit-mark-pending"
            >
              Marcar como pendiente
            </button>
          </div>
        ) : (
          <button
            onClick={() => onToggleComplete(unit.number)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#1A1A1A] text-[#FAFAFA] px-8 py-4 overline hover:bg-[#3C6348] transition-colors"
            data-testid="unit-mark-done"
          >
            <span className="inline-flex items-center justify-center w-4 h-4 border border-white/60 text-[10px] leading-none">
              ✓
            </span>
            Marcar unidad como terminada
          </button>
        )}
      </div>

      {/* Navigation */}
      <div className="mt-12 pt-10 border-t border-[#E8E1DA] flex items-center justify-between gap-4">
        <button
          disabled={activeIndex === 0}
          onClick={() => onNav(activeIndex - 1)}
          className="overline text-[#8B6D5E] hover:text-[#1A1A1A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          data-testid="manual-nav-prev"
        >
          ← Unidad anterior
        </button>
        <span className="overline text-[#5C5C5C]" data-testid="manual-nav-position">
          {String(activeIndex + 1).padStart(2, "0")} / {String(totalUnits).padStart(2, "0")}
        </span>
        <button
          disabled={activeIndex === totalUnits - 1}
          onClick={() => onNav(activeIndex + 1)}
          className="overline text-[#8B6D5E] hover:text-[#1A1A1A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          data-testid="manual-nav-next"
        >
          Siguiente unidad →
        </button>
      </div>
    </div>
  );
}
